import {Box, LoadingOverlay, TextInput} from '@mantine/core';
import {BasicButton} from '@packages/ui';
import Image from 'next/image';
import {LIIGA_HOST} from '../../../../../config/constants/hosts';
import {
  REGISTER_COMMUNITY_BENEFIT_IMAGE_PATH,
  REGISTER_COMPANY_BENEFIT_IMAGE_PATH,
  REGISTER_SCOUT_BENEFIT_IMAGE_PATH,
} from '../../../../../config/constants/img';
import {
  displayStartTime,
  maintenanceEndTime,
} from '../../../../../config/constants/maintenance';
import {INQUIRY, STUDENT_CARD} from '../../../../../config/constants/routes';
import {EMAIL_INPUT_ERROR_TYPE} from '../../../../../core/entities/register/constants';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {SectionTitle} from '../../../../../shared/components/SectionTitle';
import {createPathWithQuery} from '../../../../../shared/services/createPathWithQuery';
import {useMediaQuery} from '../../../../../shared/services/useMediaQuery';
import {MaintenanceMailAuth} from '../../../../maintenance/components/MaintenanceMailAuth';
import type {Props} from '../../MailAuthPage';

export const MailAuth: React.FC<Props> = ({
  emailInputErrorType,
  setEmailInputErrorType,
  inputEmail,
  setInputEmail,
  handleRegisterClick,
  introduceCampaignMessage,
  introduceId,
  campaignId,
  isSubmitLoading,
}) => {
  const introduceQueryParamUrl =
    introduceId && campaignId
      ? createPathWithQuery('', {
          introduce_id: introduceId,
          campaign_id: campaignId,
        })
      : '';

  const {isPC} = useMediaQuery();
  const maintenanceNow = new Date();
  const isMaintenance =
    maintenanceEndTime > maintenanceNow && maintenanceNow > displayStartTime;

  // 外資就活ネクスト 旧：LiigaのURL
  const stage = process.env.NEXT_PUBLIC_RELEASE_STAGE;
  const toLiigaURL = LIIGA_HOST[stage ?? 'production'] ?? LIIGA_HOST.production;

  return (
    <Box className="w-full max-w-[664px]">
      <LoadingOverlay
        visible={isSubmitLoading}
        overlayBlur={2}
        className="fixed"
      />
      <SectionTitle order={2} title={'新規会員登録'} />
      <Box className="relative mb-16 bg-white px-8 py-16 pc:px-32">
        {introduceId && campaignId && (
          <Box className="text-notification font-bold p-8 m-8 text-base">
            {introduceCampaignMessage}
          </Box>
        )}
        <Box className="text-left pc:text-center">
          {isMaintenance && <MaintenanceMailAuth />}
          <p className="font-bold text-md">
            会員登録には、所属大学または高等専門学校のメールアドレスが必要です。
          </p>
          <p className="text-base py-8">
            後から普段ご使用のメールアドレスに変更も可能です。
          </p>
          <CommonLink
            href={`/inquiry/foreign_univ${introduceQueryParamUrl}`}
            className="hover:underline hover:decoration-primary-2 text-primary-2 text-base"
          >
            海外の大学に通われている場合はこちら
          </CommonLink>
        </Box>

        <div className="flex p-8 justify-center">
          <TextInput
            type="email"
            className="mx-16 w-[350px]"
            placeholder="大学メールアドレス"
            error={
              emailInputErrorType === EMAIL_INPUT_ERROR_TYPE.FORMAT_ERROR
                ? 'メールアドレスの形式が不正です'
                : emailInputErrorType === EMAIL_INPUT_ERROR_TYPE.SERVER_ERROR
                  ? '認証できませんでした。ご記入に誤りが無いかご確認ください。'
                  : emailInputErrorType === EMAIL_INPUT_ERROR_TYPE.LENGTH_ERROR
                    ? '100文字以内で入力してください'
                    : emailInputErrorType ===
                        EMAIL_INPUT_ERROR_TYPE.CONFLICT_ERROR
                      ? '登録済みのメールアドレスです'
                      : ''
            }
            value={inputEmail}
            onChange={(e) => {
              setInputEmail(e.currentTarget.value);
              setEmailInputErrorType(
                e.currentTarget.value.length > 100
                  ? EMAIL_INPUT_ERROR_TYPE.LENGTH_ERROR
                  : '',
              );
            }}
            size={isPC ? 'sm' : 'md'} // アプリ&SPWebで入力時にズームされることを防ぐ
          />
        </div>
        {emailInputErrorType === 'serverError' && (
          <Box className="bg-background-1 p-8 m-8 text-base rounded">
            <ul>
              <li>
                在籍中の大学が発行したメールアドレスのみ会員登録を行うことができます。
              </li>
              <li>
                大学発行のメールアドレスをお持ちでない方は、
                <CommonLink
                  href={STUDENT_CARD}
                  className="hover:underline hover:decoration-primary-2 text-primary-2"
                  children="学生証アップロード"
                />
                で登録申請をお願いします。
              </li>
            </ul>
          </Box>
        )}
        {emailInputErrorType === 'conflictError' && (
          <Box className="bg-background-1 p-8 m-8 text-base rounded">
            <ul>
              <li>稀に大学が過去のアドレスを再利用している場合があります。</li>
              <li>
                登録に心当たりが無い場合は
                <CommonLink
                  href={INQUIRY}
                  className="hover:underline hover:decoration-primary-2 text-primary-2"
                  children="お問合せ"
                />
                ください。
              </li>
            </ul>
          </Box>
        )}
        <div className="mt-16 flex flex-col justify-center text-base">
          <div className="flex justify-center">
            <CommonLink
              href="/terms"
              className="block text-center text-primary-2 hover:underline hover:decoration-primary-2"
              target="_blank"
            >
              外資就活ドットコム利用規約
            </CommonLink>
            ・
            <CommonLink
              href="/service_privacy_policy"
              className="block text-center hover:underline hover:decoration-primary-2 text-primary-2"
              target="_blank"
            >
              プライバシーポリシー
            </CommonLink>
          </div>
          <div className="flex justify-center text-black-1">
            <CommonLink
              href={`${toLiigaURL}/terms`}
              className="block text-center hover:underline hover:decoration-primary-2 text-primary-2"
              target="_blank"
            >
              外資就活ネクスト利用規約
            </CommonLink>
            ・
            <CommonLink
              href={`${toLiigaURL}/privacy`}
              className="block text-center hover:underline hover:decoration-primary-2 text-primary-2"
              target="_blank"
            >
              個人情報保護方針
            </CommonLink>
          </div>
        </div>
        <div className="px-24 py-8 text-center">
          <BasicButton
            label="同意して登録する"
            className="pc:w-[350px] mx-auto"
            isPrimary={true}
            type="submit"
            onClick={handleRegisterClick}
          />
        </div>
        <div className="text-base text-black-1 text-center mx-8">
          <p>
            ※
            卒業後もキャリア支援サービス外資就活ネクストのアカウントとして利用できます。
          </p>
        </div>
        <div className="mt-24 p-24 text-center bg-background-1">
          <p className="text-base text-black-1">
            大学メールアドレスをお持ちでない方は、学生証をアップロードすることでもご登録いただけます。
          </p>
          <CommonLink
            href={`/student_card${introduceQueryParamUrl}`}
            className="inline-block p-8 bg-white text-primary-1 rounded border border-primary-1 text-center hover:bg-background-3 duration-200"
          >
            学生証をアップロードして登録する
          </CommonLink>
        </div>
        <div className="mt-24 flex justify-center text-base">
          <p>すでにアカウントをお持ちですか？</p>
          <CommonLink
            href="/login"
            className="block text-primary-2 hover:underline hover:decoration-primary-2"
          >
            ログインする
          </CommonLink>
        </div>
        <div className="mt-36 grid grid-cols-1 pc:grid-cols-3 gap-16 text-center">
          <div>
            <Image
              src={REGISTER_COMPANY_BENEFIT_IMAGE_PATH}
              className="mx-auto mb-16"
              width={120}
              height={120}
              alt={'company-benefit'}
            />
            <p className="text-primary-1 text-lg font-bold">
              志望企業の採用情報を見逃さない
            </p>
            <p className="text-base text-black-2">
              マッキンゼー、ゴールドマン・サックス、P&G、Microsoft、三菱商事、電通、サイバーエージェントなど、厳選企業の募集情報を受け取れます。
            </p>
          </div>
          <div>
            <Image
              src={REGISTER_SCOUT_BENEFIT_IMAGE_PATH}
              className="mx-auto mb-16"
              width={120}
              height={120}
              alt={'scout-benefit'}
            />
            <p className="text-primary-1 text-lg font-bold">
              厳選企業からの限定スカウトが受け取れる
            </p>
            <p className="text-base text-black-2">
              外資就活でしか受け取れない厳選企業からの、選考優遇のあるスカウトが多数送付されています。
            </p>
          </div>
          <div>
            <div>
              <Image
                src={REGISTER_COMMUNITY_BENEFIT_IMAGE_PATH}
                className="mx-auto mb-16"
                width={120}
                height={120}
                alt={'community-benefit'}
              />
            </div>
            <p className="text-primary-1 text-lg font-bold">
              体験記を読んで、難関企業の選考対策を
            </p>
            <p className="text-base text-black-2">
              エントリーシートや、面接で聞かれた質問など5,000本以上掲載。内定した先輩の体験記を読んで、選考突破のコツを学びましょう。
            </p>
          </div>
        </div>
      </Box>
    </Box>
  );
};
